import React, { useState, useMemo } from 'react';

import { Dialog } from '@repo/ui/Dialog';
import { Button } from '@repo/ui/Button';
import { CheckboxInputField, RangeInputField } from '@repo/ui/Form';
import { ScrollArea } from '@repo/ui/ScrollArea';
import ExportButton, { ReportFieldType } from './ExportButton';
import { Mercury } from '@repo/types/Models';

type ReportExporterProps = {
  title: string;
  children: React.ReactNode;
  filename: string;
  path: string;
  /** When false the export button is disabled with a "no content" message. */
  hasContent?: boolean;
  input: {
    query?: string;
    queryList?: Mercury.Queries.QueryList;
    first?: number;
    optionalFields: ReportFieldType[];
    requiredFields?: ReportFieldType[];
  };
};
export const TableReportExporter = ({
  filename,
  children,
  title,
  input,
  path,
  hasContent = true,
}: ReportExporterProps) => {
  //DIALOG
  const [isOpen, setIsOpen] = useState(false);

  const [rowLimit, setRowLimit] = useState<number>(100000);

  const reportfFields = input.optionalFields;

  const [deselectedFields, setDeselectedFields] = useState<{
    [field: string]: boolean;
  }>({});

  const filterFields = useMemo<ReportFieldType[]>(() => {
    return reportfFields
      .filter((reportField) => !deselectedFields[reportField.field])
      .map((reportField: ReportFieldType) => {
        return {
          as: reportField.as,
          field: reportField.field,
        };
      });
  }, [deselectedFields, reportfFields]);

  const selectAllFields = () => {
    setDeselectedFields(
      Object.fromEntries(reportfFields.map((reportField) => [reportField.field, false]))
    );
  };

  const deselectAllFields = () => {
    setDeselectedFields(
      Object.fromEntries(reportfFields.map((reportField) => [reportField.field, true]))
    );
  };

  return (
    <>
      {hasContent ? (
        <Button onClick={() => setIsOpen(true)} variant="secondary" icon="download">
          {children}
        </Button>
      ) : (
        <div className="flex items-center gap-2.5">
          <Button variant="secondary" icon="download" disabled>
            {children}
          </Button>
          <p className="text-sm opacity-70">There is no content to export.</p>
        </div>
      )}

      <Dialog
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        title={title}
        description="Select which columns you would like to include in the export."
      >
        <div className="py-5 ">
          <form className=" mb-5 flex flex-col gap-6 ">
            <ScrollArea className="overflow-hidden" viewportClassName="max-h-60 xs:max-h-none">
              <div className="grid grid-cols-1 xs:grid-cols-6 gap-2">
                {reportfFields.map((reportField) => {
                  const field = reportField.field;
                  const label = reportField.as;

                  return (
                    <CheckboxInputField
                      defaultChecked
                      key={field}
                      checked={!deselectedFields[field]}
                      label={label}
                      onChange={(e) => {
                        setDeselectedFields((oldDeselectedFields) => ({
                          ...oldDeselectedFields,
                          [field]: !e.target.checked,
                        }));
                      }}
                    />
                  );
                })}
              </div>
            </ScrollArea>

            <div className="max-w-xs flex flex-col gap-2">
              <RangeInputField
                value={rowLimit}
                min="0"
                max="100000"
                step="100"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setRowLimit(parseInt(e.target.value))
                }
                label="Limit number of rows"
                description="Only a maximum of 100,000 rows can be exported."
              />
              <p>
                Rows to export: <strong>{rowLimit}</strong>
              </p>
            </div>
          </form>
          <div className="flex items-center mt-10 ">
            <ExportButton
              path={path}
              input={{
                limit: rowLimit,
                fields: input.requiredFields
                  ? [...input.requiredFields, ...filterFields]
                  : filterFields,
                query: input.query,
                query_list: input.queryList,
              }}
              filename={filename}
              icon="download"
            >
              Export Report
            </ExportButton>
            <div className="flex ml-2.5">
              <Button variant="tertiary" onClick={selectAllFields}>
                Select all
              </Button>
              <Button variant="tertiary" onClick={deselectAllFields}>
                Unselect all
              </Button>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
};
